import { useEffect, useRef, useState } from 'react';
import { TriangleAlert, LoaderCircle, X } from 'lucide-react';

interface DestructiveActionDialogProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  confirmVariant?: 'danger' | 'warning';
  rationaleLabel?: string;
  rationalePlaceholder?: string;
  minRationaleLength?: number;
  maxRationaleLength?: number;
  onConfirm: (rationale: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * Reusable confirmation dialog for destructive admin actions (A2F-699).
 *
 * Captures a Part 11 rationale (≥ 10 chars after trim by default) before
 * calling the parent's `onConfirm`. The rationale ends up on the
 * corresponding `audit_events.rationale` column in the same transaction
 * as the mutation. Server-side validation in `RationaleRequest` is the
 * source of truth — this component's validation is convenience, not
 * security.
 *
 * State invariants:
 *   - Internal rationale state resets every time `open` transitions
 *     false → true. Parents can call `onCancel()` (or set `open=false`
 *     by other means) after a successful confirm, then reopen, and the
 *     textarea will be empty. This is an explicit acceptance criterion
 *     (A2F-699 Amendment 3) — guards against stale text leaking across
 *     unrelated actions.
 *   - The dialog does NOT auto-close on success. The parent decides
 *     what happens after `onConfirm` resolves (refresh list, show
 *     toast, close dialog).
 */
export function DestructiveActionDialog({
  open,
  title,
  description,
  confirmLabel,
  confirmVariant = 'danger',
  rationaleLabel = 'Reason',
  rationalePlaceholder = 'Why are you taking this action?',
  minRationaleLength = 10,
  maxRationaleLength = 2000,
  onConfirm,
  onCancel,
}: DestructiveActionDialogProps) {
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // A2F-699 Amendment 3: reset state on every false → true transition so a
  // previous successful submission can never leak text into a fresh dialog.
  // Keyed on `open` and only resets when transitioning to true — does not
  // run on every render.
  useEffect(() => {
    if (open) {
      setRationale('');
      setError('');
      setSubmitting(false);
      // Defer focus until the textarea is mounted.
      const id = window.setTimeout(() => textareaRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Escape closes the dialog. Listener only attached while open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        onCancel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, submitting, onCancel]);

  if (!open) return null;

  const trimmedLength = rationale.trim().length;
  const tooShort = trimmedLength < minRationaleLength;
  const showShortError = !tooShort ? false : trimmedLength > 0;
  const confirmDisabled = tooShort || submitting;

  const handleConfirm = async () => {
    if (confirmDisabled) return;
    setSubmitting(true);
    setError('');
    try {
      await onConfirm(rationale.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmClass =
    confirmVariant === 'warning'
      ? 'bg-amber-600 hover:bg-amber-700'
      : 'bg-red-600 hover:bg-red-700';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="destructive-dialog-title"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <TriangleAlert
              className={`w-5 h-5 ${confirmVariant === 'warning' ? 'text-amber-600' : 'text-red-600'}`}
            />
            <h2
              id="destructive-dialog-title"
              className="text-lg font-bold text-brand-charcoal"
            >
              {title}
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={submitting}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="text-sm text-slate-700">{description}</div>

          <div>
            <label
              htmlFor="destructive-dialog-rationale"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              {rationaleLabel} <span className="text-slate-400">(required)</span>
            </label>
            <textarea
              id="destructive-dialog-rationale"
              ref={textareaRef}
              value={rationale}
              onChange={(e) => {
                setRationale(e.target.value);
                if (error) setError('');
              }}
              placeholder={rationalePlaceholder}
              rows={3}
              maxLength={maxRationaleLength}
              disabled={submitting}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange disabled:bg-slate-50"
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={showShortError ? 'text-red-500' : 'text-transparent'}>
                Minimum {minRationaleLength} characters required
              </span>
              <span className="text-slate-400">
                {trimmedLength} / {maxRationaleLength}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              This action will be recorded in the audit log.
            </p>
          </div>

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {submitting && <LoaderCircle className="w-4 h-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
